import { useCallback, useEffect, useState } from 'react'
import {
  nextBestActionRulesApi,
  RULE_LIFECYCLE_FLAG,
  type NextBestActionRule,
  type NextBestActionRuleRequest,
} from '../api/ruleLifecycle'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button, Callout, Card, CardHeader, DataTable, DataTableEmptyRow, Dialog, FormSection, Page, PageHeader, Select,
  StatusPill, TextField, useSnackbar,
} from '../components/ui'

/**
 * R3-29 (REQ-217-001, REQ-296-001): le regole configurabili delle azioni consigliate. Col flag `regole.ciclo_vita`
 * acceso il motore delle "Prossime azioni" usa solo le regole Attive (tipo, priorita', finestre di scadenza); spento resta
 * a regole fisse. Una regola nuova nasce in Bozza e diventa operativa dopo simulazione, approvazione e pubblicazione.
 */
const KIND_LABELS: Record<string, string> = {
  richiama: 'Richiama i lead mai contattati',
  completa_pratica: 'Completa le pratiche in integrazione',
  cross_sell: 'Proponi il cross-selling',
  documento_in_scadenza: 'Documenti in scadenza',
  prodotto_in_scadenza: 'Prodotti in scadenza',
  riprogramma_appuntamento: 'Riprogramma gli appuntamenti mancati',
}

const hasWindow = (kind: string) => kind === 'documento_in_scadenza' || kind === 'prodotto_in_scadenza'

interface FormState { id: number | null; name: string; kind: string; priority: string; windowDays: string; urgentDays: string }

const EMPTY: FormState = { id: null, name: '', kind: 'richiama', priority: '', windowDays: '', urgentDays: '' }

function toNumber(v: string): number | null {
  const n = Number.parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

export function NextBestActionRulesPage() {
  const enabled = useFeatureFlag(RULE_LIFECYCLE_FLAG)
  const snackbar = useSnackbar()
  const [rows, setRows] = useState<NextBestActionRule[]>([])
  const [error, setError] = useState('')
  const [form, setForm] = useState<FormState | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setError('')
    try {
      setRows(await nextBestActionRulesApi.list())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento delle regole')
    }
  }, [])

  useEffect(() => {
    if (enabled) void load()
  }, [enabled, load])

  async function save() {
    if (!form) return
    const body: NextBestActionRuleRequest = {
      name: form.name.trim(),
      kind: form.kind,
      priority: form.priority || null,
      windowDays: hasWindow(form.kind) ? toNumber(form.windowDays) : null,
      urgentDays: hasWindow(form.kind) ? toNumber(form.urgentDays) : null,
    }
    setSaving(true)
    try {
      if (form.id === null) await nextBestActionRulesApi.create(body)
      else await nextBestActionRulesApi.update(form.id, body)
      snackbar.show({ message: 'Regola salvata' })
      setForm(null)
      await load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Salvataggio non riuscito', tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function clone(r: NextBestActionRule) {
    try {
      await nextBestActionRulesApi.clone(r.id)
      snackbar.show({ message: `Nuova versione di "${r.name}" creata in bozza` })
      await load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Copia non riuscita', tone: 'error' })
    }
  }

  const editable = (r: NextBestActionRule) => ['Bozza', 'Test', 'Simulazione', 'Approvazione'].includes(r.status)

  return (
    <Page>
      <PageHeader
        title="Regole delle azioni consigliate"
        icon="bolt"
        actions={enabled ? <Button variant="filled" icon="add" onClick={() => setForm(EMPTY)}>Nuova regola</Button> : undefined}
      />
      {!enabled && (
        <Callout tone="info">
          Le azioni consigliate usano le regole fisse. Le regole configurabili si attivano con il ciclo di vita delle regole
          (flag regole.ciclo_vita).
        </Callout>
      )}
      {error && <Callout tone="error">{error}</Callout>}
      {enabled && (
        <Card padding="none">
          <CardHeader title="Regole" subtitle="Il motore usa solo le regole Attive: un tipo senza regola Attiva non produce azioni." icon="rule" />
          <DataTable variant="plain" aria-label="Regole delle azioni consigliate">
            <thead>
              <tr>
                <th>Regola</th>
                <th>Tipo di azione</th>
                <th>Priorità</th>
                <th>Finestra</th>
                <th>Stato</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <DataTableEmptyRow colSpan={6} title="Nessuna regola" />}
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{r.name} <span className="text-muted">v{r.version}</span></td>
                  <td>{KIND_LABELS[r.kind] ?? r.kind}</td>
                  <td>{r.priority ?? 'Calcolata'}</td>
                  <td>{hasWindow(r.kind) ? `${r.windowDays ?? 60} gg (urgente entro ${r.urgentDays ?? 15})` : '—'}</td>
                  <td><StatusPill tone={r.isActive ? 'success' : 'neutral'}>{r.status}</StatusPill></td>
                  <td>
                    {editable(r)
                      ? <Button variant="text" onClick={() => setForm({
                          id: r.id, name: r.name, kind: r.kind, priority: r.priority ?? '',
                          windowDays: r.windowDays?.toString() ?? '', urgentDays: r.urgentDays?.toString() ?? '',
                        })}>Modifica</Button>
                      : r.status !== 'Archiviata' && <Button variant="text" onClick={() => void clone(r)}>Nuova versione</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Card>
      )}
      <RuleLifecyclePanel family="next-best-action" onChanged={() => void load()} />

      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        title={form?.id ? 'Modifica regola' : 'Nuova regola'}
        as="form"
        onSubmit={e => { e.preventDefault(); void save() }}
        actions={
          <>
            <Button variant="text" onClick={() => setForm(null)}>Annulla</Button>
            <Button type="submit" variant="text" disabled={saving || !form?.name.trim()}>{saving ? 'Salvataggio...' : 'Salva'}</Button>
          </>
        }
      >
        {form && (
          <FormSection title="Regola">
            <TextField label="Nome *" value={form.name} className="md-span-full"
              onChange={e => setForm(f => f && ({ ...f, name: e.target.value }))} />
            <Select label="Tipo di azione" value={form.kind} disabled={form.id !== null}
              onChange={e => setForm(f => f && ({ ...f, kind: e.target.value }))}>
              {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
            <Select label="Priorità" value={form.priority}
              onChange={e => setForm(f => f && ({ ...f, priority: e.target.value }))}>
              <option value="">Calcolata dal motore</option>
              <option value="Alta">Alta</option>
              <option value="Media">Media</option>
              <option value="Bassa">Bassa</option>
            </Select>
            {hasWindow(form.kind) && (
              <>
                <TextField type="number" label="Finestra (giorni)" value={form.windowDays} placeholder="60"
                  onChange={e => setForm(f => f && ({ ...f, windowDays: e.target.value }))} />
                <TextField type="number" label="Urgente entro (giorni)" value={form.urgentDays} placeholder="15"
                  onChange={e => setForm(f => f && ({ ...f, urgentDays: e.target.value }))} />
              </>
            )}
          </FormSection>
        )}
      </Dialog>
    </Page>
  )
}
